import { PhotoRecord } from "@/lib/catalog"
import { PreviewLoader } from "@/lib/metadata/metadata-loader"
import { PreviewSize } from "@/store/previews/previews-types"
import { fetchCurrentPreview } from "@/storage/indexeddb"
import { ImportingQueue, ImportingTaskContext, ImportingTaskPurpose } from "./importing-queue"
import { getFileForPhoto } from "../files-state"
import { getPreview, updatePreviews, visibleThumbnailsAtom } from "../previews/previews-state"
import {
	photosProcessingPriorityAtom,
	getVisibleThumbsProcessingPriorities,
} from "../processing/processing-queue-priority"
import { ProjectItem } from "../project/projects-service"
import { store } from "../store"
import { fetchOrExtractRenderedOriginalPreview } from "../previews/previews-processing"
import { getPhotoMetadata, photosAtom } from "../photo/photos-state"
import { isMetadataFinishedAtom } from "../app-state"
import { Sentry } from "@/telemetry/sentry"
import PQueue from "p-queue"
import { isAbortError } from "@/utils/error"

class PreviewImporter {
	// Queues to manage difference processing
	private previewImportingQueue = new ImportingQueue()
	private visibleRenderedPreviewQueue = new PQueue({ concurrency: 1 })

	// External data
	private startProjectId: string = ""

	private isInProgress = false
	private isPaused = false
	private renderCancellation = new AbortController()
	private activeRenders = new Set<Promise<Blob | undefined>>()

	start(project: ProjectItem) {
		if (this.startProjectId !== project.id) {
			this.cancel()
			this.isInProgress = false
		}

		if (this.isInProgress === true) {
			return
		}

		this.startProjectId = project.id
		this.isInProgress = true
		this.isPaused = false
		this.visibleRenderedPreviewQueue.start()
		this.scheduleVisibleRenderedPreviews()
	}

	cancel() {
		void this.pauseAndCancel().catch((error) => Sentry.captureException(error))
	}

	/**
	 * Async cancel that waits for in-flight tasks to complete before cleanup.
	 * Use this when you need to ensure no tasks are running after the call returns.
	 */
	async pauseAndCancel() {
		this.startProjectId = ""
		this.isInProgress = false
		const paused = this.pause()
		this.previewImportingQueue.clearTasks()
		await paused
	}

	async clearTasks(options?: { preserve?: ImportingTaskPurpose[] }) {
		this.isPaused = true
		// Wait for current task to finish before clearing
		await Promise.all([
			this.previewImportingQueue.pauseQueue(),
			this.pauseVisibleRenderedPreviews(),
		])

		this.previewImportingQueue.clearTasks(options)
	}

	async pause() {
		this.isPaused = true
		// Settle queued foreground callers, stop pending cache/metadata reads from
		// reaching the renderer, and discard results from an already-running render.
		this.renderCancellation.abort()
		this.renderCancellation = new AbortController()
		await Promise.all([
			this.previewImportingQueue.pauseQueue(),
			this.pauseVisibleRenderedPreviews(),
			Promise.allSettled([...this.activeRenders]),
		])
	}

	resumeIfNeeded() {
		this.isPaused = false
		this.scheduleVisibleRenderedPreviews()
		this.visibleRenderedPreviewQueue.start()
		if (this.previewImportingQueue.totalTasks == 0) {
			return
		}

		this.previewImportingQueue.resumeQueue()
	}

	setupSubscribers() {
		store.sub(photosProcessingPriorityAtom, () => {
			const priorities = getVisibleThumbsProcessingPriorities()
			this.previewImportingQueue.setTaskPriorities(priorities)
			if (this.visibleRenderedPreviewQueue.size === 0) return
			for (const photoId of store.get(visibleThumbnailsAtom)) {
				try {
					this.visibleRenderedPreviewQueue.setPriority(
						photoId,
						priorities.get(photoId) ?? 0,
					)
				} catch {
					// The photo is not waiting in the rendered preview queue.
				}
			}
		})
		store.sub(visibleThumbnailsAtom, () => {
			this.scheduleVisibleRenderedPreviews()
		})
		store.sub(isMetadataFinishedAtom, () => {
			this.scheduleVisibleRenderedPreviews()
		})
	}

	private scheduleVisibleRenderedPreviews() {
		this.visibleRenderedPreviewQueue.clear()
		if (this.isPaused || !this.isInProgress || !store.get(isMetadataFinishedAtom)) return

		const projectId = this.startProjectId
		const signal = this.renderCancellation.signal
		const priorities = getVisibleThumbsProcessingPriorities()
		for (const photoId of store.get(visibleThumbnailsAtom)) {
			void this.visibleRenderedPreviewQueue
				.add(
					async () => {
						if (
							signal.aborted ||
							this.startProjectId !== projectId ||
							!store.get(visibleThumbnailsAtom).has(photoId)
						)
							return

						const preview = getPreview({
							photoId,
							projectId,
							size: PreviewSize.medium,
						})
						if (preview?.tags.includes("rendered") && preview.tags.includes("original"))
							return

						const photo = store.get(photosAtom)[photoId]
						const metadata = getPhotoMetadata(photoId)
						if (!photo || !metadata) return

						// Original preview rendering acquires the shared AI editor lock.
						await this.renderPreview({
							photo,
							metadata,
							projectId,
							signal,
						})
					},
					{ id: photoId, priority: priorities.get(photoId) ?? 0 },
				)
				.catch((error) => {
					if (!isAbortError(error)) Sentry.captureException(error)
				})
		}
	}

	private async pauseVisibleRenderedPreviews() {
		this.visibleRenderedPreviewQueue.pause()
		this.visibleRenderedPreviewQueue.clear()
		await this.visibleRenderedPreviewQueue.onPendingZero()
	}

	private async renderPreview(
		props: Parameters<typeof fetchOrExtractRenderedOriginalPreview>[0],
	) {
		const render = fetchOrExtractRenderedOriginalPreview(props)
		this.activeRenders.add(render)
		try {
			return await render
		} finally {
			this.activeRenders.delete(render)
		}
	}

	extractFastPreviewThumbnail(photo: PhotoRecord) {
		this.previewImportingQueue.addTask({
			id: photo.id,
			purpose: "fast-preview",
			handler: async () => {
				const info = {
					photo,
					photoId: photo.id,
					projectId: this.startProjectId,
					size: PreviewSize.small,
				}
				const existingPreview = getPreview(info)

				if (existingPreview) {
					return
				}

				const existingPreviewRecord = await fetchCurrentPreview(info)
				if (existingPreviewRecord) {
					void updatePreviews({
						previewsInfo: [
							{
								...info,
								data: existingPreviewRecord.data,
								tags: existingPreviewRecord.tags,
							},
						],
						skipStoreToDB: true,
					})
					return
				}

				const file = getFileForPhoto(photo)
				if (!file) {
					return
				}

				const preview = await PreviewLoader.getImage(file, PreviewSize.small)

				if (preview) {
					void updatePreviews({
						previewsInfo: [
							{
								...info,
								data: preview,
								tags: ["extracted", "original"],
							},
						],
					})
				}
			},
		})
	}

	async extractRenderedPreviewOOB(context: ImportingTaskContext): Promise<Blob | undefined> {
		if (this.isPaused) return
		const projectId = this.startProjectId
		const signal = this.renderCancellation.signal
		return new Promise((resolve) => {
			// Queue clearing must also settle callers waiting on a foreground preview.
			const onCancel = () => resolve(undefined)
			signal.addEventListener("abort", onCancel, { once: true })
			this.previewImportingQueue.addTask({
				id: context.photo.id,
				purpose: "slow-preview-foreground",
				handler: async () => {
					try {
						const result = await this.renderPreview({
							...context,
							projectId,
							signal,
						})
						resolve(result)
					} catch (e) {
						resolve(undefined)
						if (!isAbortError(e)) throw e
					} finally {
						signal.removeEventListener("abort", onCancel)
					}
				},
			})
		})
	}
}

export const previewImporter = new PreviewImporter()
